/** @jsxImportSource @emotion/react */
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import dayjs from 'dayjs';
import { CellType } from '@grapecity/wijmo.grid';
import { InputDate } from '@grapecity/wijmo.input';
import { Workbook } from '@grapecity/wijmo.xlsx';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Button } from '@mui/material';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { tabs } from 'components/layouts/Tab';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { CommonUtil } from 'utils/CommonUtil';
import PropTypes from 'prop-types';
import { getExcelFileName } from 'utils/ExcelUtil';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import { useLoading } from 'components/process/Loading';
import { downloadExcelTemplatesPost, uploadExcelUtMatrixTemplates } from 'apis/common/Excel';
import { useMessageBar } from 'hooks/useMessageBar';
import ExcelValidModal from 'components/modals/common/ExcelValidModal';
import { FileTypeName } from 'models/common/Common';
import { findExcpByElmPrjId, findScheduleRequest } from 'apis/setup/SetupSchedule';
import { tb } from 'components/layouts/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import { SetupProject } from 'models/setup/SetupProject';
import { findProjectById } from 'apis/setup/SetupProject';
import { SubTitleGroup } from 'components/layouts/ContentLayout';
import { SetupSchedule, SetupTargetStepScheduleRequest } from 'models/setup/SetupSchedule';
import { useCommonModal } from 'hooks/useCommonModal';
import { CommonText } from 'ui/theme/Color';
interface Props {
  pageId: string;
  aprReqId: string;
  elmPrjId: string;
  excpId?: string;
  requestList?: any[]; // 일정변경요청인 경우 변경대상설비 선택
  isReadOnly?: boolean;
}

/**
 * @desc 설비셋업관리 > 프로젝트 셋업관리 > 프로젝트 일정 결재요청 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jshwang12     신규 생성
 * 2025.11.03   jshwang12     해당 프로젝트에 등록된 설비목록 노출 (임시저장된 계획일정있으면 노출)
 * 2025.11.03   jshwang12     Down ID 없으면 '최신 템플릿을 다운받아 주세요.'  있으면 '최신 템플릿 사용중입니다.' 문구 노출
 * 2025.11.14   jshwang12     일정 변경/등록 화면 통합,  일정 등록/변경 엑셀 양식 동일하게 적용 (상태/계획일정 색상 등으로 신규등록/일정변경 구분)
 * 2026.01.28   jshwang12     일정등록/수정 요청 UI 수정 기능 추가
 * 2026.03.04   jihwan89      엑셀 다운로드 시 type: MODAL 추가
 */
const SetupScheduleRequest = (props: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const [code, setCode] = useState<any>();
  const [project, setProject] = useState<SetupProject>();
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule>();
  const [rowData, setRowData] = useState<any>();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [excelValidCondition, setExcelValidCondition] = useState<string | null>(null);
  const [isExcelValidModalOpen, setExcelValidModalOpen] = useState<boolean>(false);
  const dateEditor = useRef(
    new InputDate(document.createElement('div'), {
      format: 'yyyy.MM.dd',
      isRequired: false,
    })
  );

  useImperativeHandle(ref, () => ({
    getBizReqData: (aprReqId: string, reqRsn: string) => {
      // 저장
      const master = {
        castType: 'SetupScheduleRequest', // 서버측 결재요청상세vo > bizReqData cast 타입
        aprReqId: aprReqId,
        chgRsnCtn: reqRsn,
        elmPrjId: props.elmPrjId,
        requestList: rowData,
      } as SetupTargetStepScheduleRequest;
      return master;
    },
    validate: () => {
      if (props?.isReadOnly) {
        return true;
      }

      if (findEmptyPlanDt().length) {
        openCommonModal({
          content: t(
            'setup.label.모든 단계의 일정을 입력해 주세요.',
            '모든 단계의 일정을 입력해 주세요.'
          ),
        });
        return false;
      }
      return true;
    },
  }));

  const findEmptyPlanDt = () => {
    return Object.entries(rowData)
      .map(([spTpCd, rows]) =>
        (rows as any[])
          .map((row, idx) =>
            (code[spTpCd] || [])
              .map((cd) => {
                const stDt = row[`'${cd.cmnCd}'_NEW_PLAN_ST_DT`] || row[`'${cd.cmnCd}'_PLAN_ST_DT`];
                const endDt =
                  row[`'${cd.cmnCd}'_NEW_PLAN_END_DT`] || row[`'${cd.cmnCd}'_PLAN_END_DT`];
                if (!stDt || !endDt) {
                  return { index: idx, spTpCd: spTpCd, prjSpCd: cd.cmnCd, prjSpCdNm: cd.cmnCdNm };
                }
                return null;
              })
              .filter((o) => !!o)
          )
          .filter((o) => o.length)
      )
      .filter((o) => o.length)
      .flat();
  };

  const tabMenus = useMemo(() => {
    return (code?.spTpCd || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          id: cur.cmnCd,
          name: cur.cmnCdNm,
        },
      ],
      []
    );
  }, [code?.spTpCd]);

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (props?.elmPrjId) {
      findProjectById(props.elmPrjId).then((result: SetupProject) => {
        if (!result) {
          openCommonModal({
            content: t('setup.msg.프로젝트 정보가 없습니다.', '프로젝트 정보가 없습니다.'),
          });
          return;
        }
        setProject(result);
      });
    } else {
      setProject(undefined);
    }
  }, [props?.elmPrjId]);

  useEffect(() => {
    searchExcp();
  }, [props?.elmPrjId, props?.excpId]);

  useEffect(() => {
    handleSearch();
  }, [props?.elmPrjId, props?.excpId, props?.aprReqId, code?.spTpCd]); // currentTabIndex

  const searchExcp = () => {
    if (props?.elmPrjId && props?.excpId) {
      findExcpByElmPrjId(props.elmPrjId, props.excpId).then((result) => {
        setPrjExcpData((result || []).length > 0 ? result[0] : undefined);
      });
    } else {
      setPrjExcpData(undefined);
    }
  };

  const getCommonCodes = async () => {
    const spTpCd: Code[] = await getCommonCodeNames('SP_TP_CD');
    const prjSpCds = { spTpCd: spTpCd } as any;
    if ((spTpCd || []).length) {
      await Promise.all(
        spTpCd.map(async (o) => {
          if (o.cmnCd) {
            prjSpCds[o.cmnCd] = await getCommonCodeNamesCondition({
              cmnGrCd: o.cmnCd,
              optValCtn5: 'Y', // 일정상세표시여부
            });
          }
        })
      );
    }
    setCode({
      ...prjSpCds,
    });
  };

  const handleSearch = () => {
    if (props.aprReqId && props?.elmPrjId && props?.excpId) {
      findScheduleRequest(
        props.aprReqId,
        props.elmPrjId,
        props.excpId,
        undefined, // tabMenus?.[currentTabIndex]?.id
        props?.requestList
      ).then((result) => {
        setRowData(result);
      });
    } else {
      setRowData({});
    }
  };

  const downloadExcelTemplate = async () => {
    if (!props?.elmPrjId) {
      openCommonModal({
        content: t('setup.label.프로젝트ID가 없습니다.', '프로젝트ID가 없습니다.'),
      });
      return;
    }
    if (!props?.excpId) {
      openCommonModal({
        content: t('setup.label.협력업체ID가 없습니다.', '협력업체ID가 없습니다.'),
      });
      return;
    }
    const excps = await findExcpByElmPrjId(props.elmPrjId, props.excpId);
    const excp = (excps || []).length > 0 ? excps[0] : undefined;
    if (!excp) {
      openCommonModal({
        content: t('setup.label.협력업체 정보가 없습니다.', '협력업체 정보가 없습니다.'),
      });
      return;
    }
    if (!excp?.excpAbbrNm) {
      openCommonModal({
        content: t(
          'setup.label.먼저 협력업체 약어를 등록해 주세요.',
          '먼저 협력업체 약어를 등록해 주세요.'
        ),
      });
      return;
    }
    openLoading(true);
    downloadExcelTemplatesPost(
      FileTypeName.TPL_SETUP_SCHD_LIST,
      props.elmPrjId,
      (props?.requestList || []).map((o) => o.tgtEqpId), // 일정변경요청인 경우 변경요청 대상 설비 목록 (임시저장 전 상태 사용)
      props.excpId,
      props.pageId,
      props.aprReqId
    ).finally(() => {
      if (!prjExcpData?.planReqId) {
        searchExcp();
      }
      openLoading(false);
    });
  };

  const handleDownloadExcel = () => {
    const workbook = new Workbook();
    for (const spTpCd of code?.spTpCd || []) {
      const ws = CommonUtil.createWijmoSheet(
        spTpCd.cmnCdNm,
        mapLayoutDefinition[spTpCd.cmnCd],
        rowData[spTpCd.cmnCd],
        'MODAL'
      );
      workbook.sheets.push(ws);
    }
    workbook.saveAsync(getExcelFileName(t('setup.label.일정현황 상세', '일정현황 상세')));
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col]?.binding;
      const item = panel.rows[row]?.dataItem;
      if (
        binding &&
        binding !== 'TGT_PLAN_END_DT' &&
        (binding.endsWith('_NEW_PLAN_ST_DT') || binding.endsWith('_NEW_PLAN_END_DT'))
      ) {
        cell.ariaReadOnly = props?.isReadOnly || false;
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col]?.binding;
    const item = grid.rows[e.row]?.dataItem;
    if (
      binding &&
      binding !== 'TGT_PLAN_END_DT' &&
      (binding.endsWith('_NEW_PLAN_ST_DT') || binding.endsWith('_NEW_PLAN_END_DT'))
    ) {
      if (props?.isReadOnly) {
        e.cancel = true;
      } else {
        item[binding] = item[binding] || item[binding.replace('_NEW', '')];
      }
    }
  });

  const onCellEditEnding = (grid, e) => {
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row]?.dataItem;
    const binding = grid.columns[e.col]?.binding;

    if (
      binding &&
      binding !== 'TGT_PLAN_END_DT' &&
      (binding.endsWith('_NEW_PLAN_ST_DT') || binding.endsWith('_NEW_PLAN_END_DT'))
    ) {
      if (newVal) {
        const newDate = dayjs(newVal);
        if (!newDate.isValid()) {
          openCommonModal({
            content: t('setup.label.날짜를 정확히 입력해 주세요.', '날짜를 정확히 입력해 주세요.'),
          });
          e.cancel = true;
          return;
        }
        if (binding.endsWith('_NEW_PLAN_ST_DT')) {
          const keyNewEndDt = binding.replace('_NEW_PLAN_ST_DT', '_NEW_PLAN_END_DT');
          const keyEndDt = binding.replace('_NEW_PLAN_ST_DT', '_PLAN_END_DT');
          const endDt = data?.[keyNewEndDt] || data?.[keyEndDt];
          if (endDt && dayjs(endDt).isBefore(newDate)) {
            openCommonModal({
              content: t(
                'setup.label.종료일이 시작일보다 빠를수 없습니다.',
                '종료일이 시작일보다 빠를수 없습니다.'
              ),
            });
            e.cancel = true;
            return;
          }
        }
        if (binding.endsWith('_NEW_PLAN_END_DT')) {
          const keyNewStDt = binding.replace('_NEW_PLAN_END_DT', '_NEW_PLAN_ST_DT');
          const keyStDt = binding.replace('_NEW_PLAN_END_DT', '_PLAN_ST_DT');
          const stDt = data?.[keyNewStDt] || data?.[keyStDt];
          if (stDt && dayjs(stDt).isAfter(newDate)) {
            openCommonModal({
              content: t(
                'setup.label.종료일이 시작일보다 빠를수 없습니다.',
                '종료일이 시작일보다 빠를수 없습니다.'
              ),
            });
            e.cancel = true;
            return;
          }
          // [26.03.03] 일정 검증 추가 (타겟일정 >= 종료일)
          const tgtDt = data?.['TGT_PLAN_END_DT'];
          if (tgtDt && dayjs(tgtDt).isBefore(newDate)) {
            openCommonModal({
              content: t(
                'setup.label.일정은 타겟일정보다 같거나 빨라야 합니다.<br/>설비별 타겟일정을 확인해 주세요.',
                '일정은 타겟일정보다 같거나 빨라야 합니다.<br/>설비별 타겟일정을 확인해 주세요.'
              ),
            });
            e.cancel = true;
            return;
          }
        }
      }
      data[binding] = newVal || null;
    }
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
  };

  const mapLayoutDefinition = useMemo(() => {
    const getPlanDt = (item, prjSpCd, isStartDt = true) => {
      let planDt = item[`'${prjSpCd}'_PLAN_${isStartDt ? 'ST' : 'END'}_DT`];
      let newPlanDt = item[`'${prjSpCd}'_NEW_PLAN_${isStartDt ? 'ST' : 'END'}_DT`];
      planDt = planDt ? dayjs(planDt).format('YYYY.MM.DD') : null;
      newPlanDt = newPlanDt ? dayjs(newPlanDt).format('YYYY.MM.DD') : null;

      // 퍼블 작업 전 임시적용
      const styles = {
        NEW: `color: ${CommonText.Primary}; font-weight: 700;`,
        CHG: `color: ${CommonText.Error}; font-weight: 700;`,
      };
      const type = !newPlanDt
        ? 'SCHD'
        : !planDt && newPlanDt
        ? 'NEW'
        : planDt !== newPlanDt
        ? 'CHG'
        : '';
      // [25.12.02] 툴팁에는 변경전 날짜 노출
      return `<span data-tooltip="${planDt || newPlanDt || ''}" style="${styles[type] || ''}" >${
        newPlanDt || planDt || ''
      }</span>`;
    };

    const getTgtDt = (item) => {
      const rowType = item['ROW_TYPE'];
      const tgtDt = item[`TGT_PLAN_END_DT`];
      // 퍼블 작업 전 임시적용
      const styles = {
        CHG: `color: ${CommonText.Error}; font-weight: 700;`,
      };
      const type = rowType === 'TGT_CHG' ? 'CHG' : '';
      return `<span style="${styles[type] || ''}" >${tgtDt}</span>`;
    };

    return (code?.spTpCd || []).reduce((acc, cur) => {
      const prjSpLayout = (code?.[cur.cmnCd] || []).reduce(
        (acc, cur, idx) => [
          ...acc,
          {
            header: cur.cmnCdNm,
            columns: [
              {
                header: String(t('setup.label.계획 시작일', '계획 시작일')),
                binding: `'${cur.cmnCd}'_NEW_PLAN_ST_DT`,
                minWidth: 80,
                width: props?.isReadOnly ? 100 : 110,
                isRequired: false,
                isReadOnly: props?.isReadOnly,
                align: props?.isReadOnly ? 'center' : 'left',
                cssClass: props?.isReadOnly ? '' : 'WijmoDate',
                editor: dateEditor.current,
                cellTemplate: (params) => getPlanDt(params.item, cur.cmnCd),
              },
              {
                header: String(t('setup.label.계획 종료일', '계획 종료일')),
                binding: `'${cur.cmnCd}'_NEW_PLAN_END_DT`,
                minWidth: 80,
                width: props?.isReadOnly ? 100 : 110,
                isRequired: false,
                isReadOnly: props?.isReadOnly,
                align: props?.isReadOnly ? 'center' : 'left',
                cssClass: props?.isReadOnly ? '' : 'WijmoDate',
                editor: dateEditor.current,
                cellTemplate: (params) => getPlanDt(params.item, cur.cmnCd, false),
              },
            ],
          },
        ],
        []
      );
      return {
        ...acc,
        [cur.cmnCd]: [
          {
            header: String(t('com.label.요청유형', '요청유형')),
            binding: 'ROW_TYPE',
            minWidth: 70,
            width: 78,
            isReadOnly: true,
            align: 'center',
            visible: !props?.isReadOnly,
            cellTemplate: (params) =>
              params.value === 'EQP' || params.value === 'NEW'
                ? t('setup.label.일정등록', '일정등록')
                : params.value === 'CHG' || params.value === 'TGT_CHG'
                ? t('setup.label.일정변경', '일정변경')
                : '',
          },
          {
            header: String(t('setup.label.공정', '공정')),
            binding: 'PRDN_PROC_CD_NM',
            minWidth: 120,
            width: 120,
            align: 'center',
            isReadOnly: true,
          },
          {
            header: String(t('com.label.설비군', '설비군')),
            binding: 'EQCL_ID',
            minWidth: 70,
            width: 90,
            align: 'center',
            isReadOnly: true,
          },
          {
            header: String(t('setup.label.양/음극', '양/음극')),
            binding: 'ELTR_TP_CD_NM',
            minWidth: 80,
            width: 100,
            align: 'center',
            isReadOnly: true,
          },
          {
            header: String(t('setup.label.호기', '호기')),
            binding: 'EQP_LN_ID',
            minWidth: 80,
            width: 80,
            align: 'center',
            isReadOnly: true,
          },
          {
            header: String(t('setup.label.M-CODE', 'M-CODE')),
            binding: 'EQP_ID',
            minWidth: 140,
            width: 140,
            align: 'center',
            isReadOnly: true,
          },
          {
            header: String(t('setup.label.설비명', '설비명')),
            binding: 'EQP_NM',
            minWidth: 140,
            width: '*',
            align: 'left',
            isReadOnly: true,
          },
          {
            header: String(t('setup.label.협력업체명', '협력업체명')),
            binding: 'EXCP_NM',
            minWidth: 140,
            width: 140,
            align: 'left',
            isReadOnly: true,
          },
          {
            header: String(t('setup.label.타겟일정', '타겟일정')),
            binding: 'TGT_PLAN_END_DT',
            minWidth: 80,
            width: 100,
            align: 'center',
            isReadOnly: true,
            cellTemplate: (params) => getTgtDt(params.item),
          },
          ...prjSpLayout,
        ],
      };
    }, {});
  }, [code, props?.isReadOnly]);

  const filterRowData = useMemo(() => {
    const spTpCd = tabMenus?.[currentTabIndex]?.id;
    if (rowData && spTpCd) {
      if (Object.keys(rowData || {}).filter((key) => key === spTpCd)) {
        return rowData[spTpCd];
      }
    }
    return [];
  }, [rowData, currentTabIndex]);

  const includeFilter = [
    'ROW_TYPE',
    'PRDN_PROC_CD_NM',
    'EQCL_ID',
    'ELTR_TP_CD_NM',
    'EQP_LN_ID',
    'EQP_ID',
    'EQP_NM',
    'EXCP_NM',
    'TGT_PLAN_END_DT',
  ];

  return (
    <>
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span>{String(t('setup.label.플랜트', '플랜트'))}</span>
              </TableCell>
              <TableCell>
                <span>{project?.bildPlntCdNm}</span>
              </TableCell>
              <TableCell>
                <span>{String(t('setup.label.구분', '구분'))}</span>
              </TableCell>
              <TableCell>
                <span>{project?.prjDivsCdNm}</span>
              </TableCell>
              <TableCell>
                <span>{String(t('setup.label.프로젝트 ID', '프로젝트 ID'))}</span>
              </TableCell>
              <TableCell>
                <span>{project?.elmPrjId}</span>
              </TableCell>
              <TableCell>
                <span>{String(t('setup.label.프로젝트 기간', '프로젝트 기간'))}</span>
              </TableCell>
              <TableCell>
                {CommonUtil.formatPeriod(project?.ivesStDt ?? '', project?.ivesEndDt ?? '')}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span>{String(t('setup.label.프로젝트명', '프로젝트명'))}</span>
              </TableCell>
              <TableCell colSpan={7}>{project?.elmPrjNm}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <div css={tabs.wrap} style={{ marginTop: '12px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              className={(rowData?.[o.id] || []).length > 0 ? 'tab-bg' : ''} // [26.01.30] tab 배경색 추가 (데이터 있는 경우 미리 탭으로 구분하기 위함)
              id={`setup-schedule-request-tab-${idx}`}
              aria-controls={`setup-schedule-request-tabpanel-${idx}`}
            />
          ))}
          {!props.isReadOnly && (
            <SubTitleGroup>
              <div className="info warning">
                {prjExcpData?.planReqId
                  ? '최신 템플릿 사용중입니다.'
                  : '최신 템플릿을 다운받아 주세요.'}
              </div>
            </SubTitleGroup>
          )}
        </Tabs>
        <div css={tabs.btn}>
          {!props.isReadOnly && (
            <>
              <Button
                css={IconButton.button}
                className="Exceldown"
                onClick={downloadExcelTemplate}
                disableRipple
              >
                {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
              </Button>
              <ExcelUploadButton
                msgConfirm={
                  t(
                    'setup.label.저장된 데이터가 있으면 기존 데이터는 삭제됩니다.\n 새로 업로드 하시겠습니까?',
                    '저장된 데이터가 있으면 기존 데이터는 삭제됩니다.\n 새로 업로드 하시겠습니까?'
                  ) || ''
                }
                onPreCheck={async () => {
                  const result = await findExcpByElmPrjId(props.elmPrjId, props.excpId);
                  const excp = (result || []).length > 0 ? result[0] : undefined;
                  if (!excp?.planReqId) {
                    openCommonModal({
                      content: String(
                        t(
                          'setup.label.설비가 추가 및 변경되었으니 최신 템플릿을 다운로드 받아주세요.',
                          '설비가 추가 및 변경되었으니 최신 템플릿을 다운로드 받아주세요.'
                        )
                      ),
                    });
                    searchExcp();
                    return false;
                  }
                  return true;
                }}
                onCallback={(file) => {
                  openLoading(true);
                  uploadExcelUtMatrixTemplates(
                    file,
                    FileTypeName.TPL_SETUP_SCHD_LIST,
                    'NORMAL',
                    'TB_EELMB_PRJ_TGT_SP_SCHD_M',
                    props.elmPrjId,
                    props.excpId,
                    props.aprReqId
                  )
                    .then((response) => {
                      if (response?.data.x_result == 'OK') {
                        openMessageBar({
                          type: 'confirm',
                          content: t(
                            'setup.label.검증이 완료되었습니다.',
                            '검증이 완료되었습니다.'
                          ),
                        });
                        handleSearch();
                      } else {
                        setExcelValidCondition(response?.data.p_xls_upload_id);
                        setExcelValidModalOpen(true);
                      }
                    })
                    .finally(() => {
                      openLoading(false);
                    });
                }}
              />
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={handleSearch}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </div>
      </div>
      {tabMenus.map((o, idx) => (
        <TabPanel key={o.id} value={currentTabIndex} index={idx}>
          <CustomGrid
            id={`schdRequestGrid_${o.id}`}
            layoutDefinition={mapLayoutDefinition[o.id]}
            rowData={filterRowData}
            height={300}
            isSelector={false}
            isFilter={false}
            allowPinning={false}
            allowSorting={false}
            allowDragging={false}
            initialized={onInitialized}
            beginningEdit={onBeginningEdit}
            cellEditEnding={onCellEditEnding}
          />
        </TabPanel>
      ))}
      {isExcelValidModalOpen && (
        <ExcelValidModal
          open={isExcelValidModalOpen}
          close={() => {
            setExcelValidCondition(null);
            setExcelValidModalOpen(false);
          }}
          condition={{
            valReqId: excelValidCondition || '',
          }}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-request-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default forwardRef(SetupScheduleRequest);
